import {useCallback} from 'react';
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowDownward,
    ArrowUpward,
    Delete,
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    VerticalAlignBottom,
    VerticalAlignTop,
} from '@mui/icons-material';
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import CMemoryColorPicker
    from 'crqlar-react-common/dist/components/CMemoryColorPicker';
import {FabricObject} from 'fabric';
import {FloorPlanItem, TextElement} from '../types';
import {FABRIC_DEFAULTS} from "../utils/fabric-defaults";
import {useEditConfiguration} from "../hooks/useEditConfiguration";
import {useEditableTextField} from "../hooks/useEditableTextField";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useLiveScaledDimensions} from "../hooks/useLiveScaledDimensions";
import {
    getNormalizedTextCanvas,
    getTextDisplayDimensions,
} from "../utils/resize-utils";

interface Props {
    element: FabricObject;
    onBack: () => void;
    onElementUpdated: () => void;
}

export const EditTextStyles: React.FC<Props> = ({
                                                    element,
                                                    onBack,
                                                    onElementUpdated,
                                                }) => {
    const {t} = useTranslation();
    const {canvas} = useFloorplanContext();

    const item = element.floorPlanItem!;
    const elementData = item.element as TextElement;
    const elementCanvas = elementData.canvas;

    const {
        rebuild,
        position,
        handleUpdatePosition,
        rotation,
        handleRotationChange,
        updateSelectable,
        handleBringToFront,
        handleSendToBack,
        handleBringForward,
        handleSendBackward,
        handleDelete,
    } = useEditConfiguration({canvas, element, onBack, onElementUpdated});

    const buildUpdatedItem = useCallback((updates: Record<string, any>): FloorPlanItem => {
        return {
            ...item,
            element: {
                ...elementData,
                canvas: {
                    ...getNormalizedTextCanvas(elementData.canvas, element.scaleX, element.scaleY),
                    ...updates,
                }
            },
        };
    }, [element.scaleX, element.scaleY, elementData, item]);

    const handleUpdate = useCallback((updates: Record<string, any>) => {
        rebuild(buildUpdatedItem(updates));
    }, [rebuild, buildUpdatedItem]);

    const getDimensions = useCallback((target: FabricObject) => getTextDisplayDimensions(target), []);
    const scalingOverride = useLiveScaledDimensions(canvas, element, getDimensions);
    const dimensions = scalingOverride ?? getDimensions(element);

    const handleSelectableChange = useCallback((selectable: boolean) => {
        updateSelectable(selectable);
        handleUpdate({selectable});
    }, [updateSelectable, handleUpdate]);

    const textField = useEditableTextField({
        element,
        initialValue: elementCanvas.text ?? '',
        onSave: (value) => handleUpdate({text: value}),
        onUnmountSave: (value, elementId) => {
            const currentElement = elementId
                ? canvas?.getObjects().find(obj => obj.floorPlanItem?.id === elementId)
                : null;
            const data = currentElement?.floorPlanItem?.element as TextElement | undefined;
            if (data) {
                data.canvas.text = value;
                (currentElement as any).set('text', value);
                currentElement?.canvas?.renderAll();
            }
        },
    });

    const isDisabled = elementCanvas?.selectable === false;

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Text Content')}</Typography>
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={textField.value}
                    onChange={(e) => textField.onChange(e.target.value)}
                    onBlur={textField.onBlur}
                    disabled={isDisabled}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Font Size')}</Typography>
                <CNumberField
                    size="small"
                    fullWidth
                    value={elementCanvas.fontSize ?? FABRIC_DEFAULTS.text.fontSize}
                    onChange={(v: number | null) => v !== null && v >= 8 && handleUpdate({fontSize: v})}
                    min={8}
                    max={200}
                    disabled={isDisabled}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Font Family')}</Typography>
                <FormControl size="small" fullWidth disabled={isDisabled}>
                    <Select
                        value={elementCanvas.fontFamily ?? FABRIC_DEFAULTS.text.fontFamily}
                        onChange={(e) => handleUpdate({fontFamily: e.target.value})}
                    >
                        <MenuItem value="sans-serif">{t('Sans Serif')}</MenuItem>
                        <MenuItem value="serif">{t('Serif')}</MenuItem>
                        <MenuItem value="monospace">{t('Monospace')}</MenuItem>
                        <MenuItem value="Arial">Arial</MenuItem>
                        <MenuItem value="Georgia">Georgia</MenuItem>
                        <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Style')}</Typography>
                <Stack direction="row" spacing={1}>
                    <ToggleButtonGroup size="small" disabled={isDisabled}>
                        <ToggleButton
                            value="bold"
                            selected={elementCanvas.fontWeight === 'bold'}
                            onClick={() => handleUpdate({
                                fontWeight: elementCanvas.fontWeight === 'bold' ? 'normal' : 'bold',
                            })}
                        >
                            <FormatBold/>
                        </ToggleButton>
                        <ToggleButton
                            value="italic"
                            selected={elementCanvas.fontStyle === 'italic'}
                            onClick={() => handleUpdate({
                                fontStyle: elementCanvas.fontStyle === 'italic' ? 'normal' : 'italic',
                            })}
                        >
                            <FormatItalic/>
                        </ToggleButton>
                        <ToggleButton
                            value="underline"
                            selected={!!elementCanvas.underline}
                            onClick={() => handleUpdate({underline: !elementCanvas.underline})}
                        >
                            <FormatUnderlined/>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            sx={{mb: 1.5}}>{t('Position')}</Typography>
                <Stack direction="row" spacing={1}>
                    <CNumberField
                        size="small"
                        label={t('Left')}
                        value={Math.round(position.x)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(v, position.y)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                    <CNumberField
                        size="small"
                        label={t('Top')}
                        value={Math.round(position.y)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(position.x, v)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                </Stack>
            </Box>

            <Box>
                <Tooltip
                    title={t('Size is determined by font size and text content')}
                    arrow>
                    <Typography variant="subtitle2" sx={{
                        mb: 1.5,
                        textDecoration: 'underline',
                        textDecorationStyle: 'dashed',
                        textUnderlineOffset: '3px',
                        cursor: 'help',
                        width: 'fit-content',
                    }}>{t('Size')}</Typography>
                </Tooltip>
                <Stack direction="row" spacing={1}>
                    <CNumberField
                        size="small"
                        label={t('Width')}
                        value={Math.round(dimensions.width)}
                        onChange={() => {
                        }}
                        sx={{flex: 1}}
                        disabled
                    />
                    <CNumberField
                        size="small"
                        label={t('Height')}
                        value={Math.round(dimensions.height)}
                        onChange={() => {
                        }}
                        sx={{flex: 1}}
                        disabled
                    />
                </Stack>
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Rotation')}</Typography>
                <CNumberField
                    size="small"
                    fullWidth
                    value={Math.round(rotation)}
                    onChange={(v: number | null) => handleRotationChange(v ?? undefined)}
                    min={-360}
                    max={360}
                    disabled={isDisabled}
                />
            </Box>

            <Divider/>

            <Stack direction="row" spacing={1.5}
                   sx={{opacity: isDisabled ? 0.5 : 1}}>
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Color')}</Typography>
                    <CMemoryColorPicker
                        color={elementCanvas?.fill as string ?? FABRIC_DEFAULTS.text.fill}
                        onChange={(color) => handleUpdate({fill: color})}
                        disabled={isDisabled}
                    />
                </Box>
            </Stack>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Interaction')}</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={elementCanvas?.selectable !== false}
                            onChange={(e) => handleSelectableChange(e.target.checked)}
                        />
                    }
                    label={<Typography
                        variant="body2">{t('Selectable on canvas')}</Typography>}
                />
            </Box>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Layer')}</Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('Bring to Front')}>
                        <span>
                            <IconButton size="small" onClick={handleBringToFront}
                                        disabled={isDisabled}>
                                <VerticalAlignTop fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Bring Forward')}>
                        <span>
                            <IconButton size="small" onClick={handleBringForward}
                                        disabled={isDisabled}>
                                <ArrowUpward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send Backward')}>
                        <span>
                            <IconButton size="small" onClick={handleSendBackward}
                                        disabled={isDisabled}>
                                <ArrowDownward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send to Back')}>
                        <span>
                            <IconButton size="small" onClick={handleSendToBack}
                                        disabled={isDisabled}>
                                <VerticalAlignBottom fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            <Divider/>

            <Box>
                <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<Delete/>}
                    onClick={handleDelete}
                >
                    {t('Delete Text')}
                </Button>
            </Box>
        </Stack>
    );
};
